import {
  bbox,
  insideBuildingWithMargin,
  orientedRectCorners,
  pointInPolygon,
  rotP,
} from '../geometry';
import type { OrientedRect, ParkingSlot, Point } from '../types';

// 車が実際に通れるかを、通路の「描画上の矩形」ではなく空間そのもので判定する到達チェック。
// 敷地から建物と駐車マスを除いた範囲のうち、通路として最低限の幅(一方通行の車路幅)が取れる
// 部分だけを走行可能とみなし、出入口からたどり着ける範囲を求める。各マスは、短辺側(出入口側)の
// すぐ前がその範囲に入っていれば到達可能とする。

/** 走行可能とみなす最小の通路幅(m)。一方通行の車路幅の目安 */
export const ACCESS_MIN_LANE_WIDTH_M = 3.5;
const ACCESS_CELL_M = 0.5;
const ACCESS_MAX_CELLS = 120000;

export interface AccessBase {
  x0: number;
  y0: number;
  cell: number;
  nx: number;
  ny: number;
  /** 1 = 敷地外または建物(マスを置く前から通れない) */
  blocked: Uint8Array;
  /** blockedからの距離(m)。通り道の計画用に必要になったときだけ作る */
  baseDist?: Float32Array;
}

/** 敷地・建物だけで決まる通行不可マスク。同じ敷地に対して何度も判定するので、先に一度だけ作る */
export function buildAccessBase(sitePts: Point[], buildings: OrientedRect[]): AccessBase {
  const bb = bbox(sitePts);
  let cell = ACCESS_CELL_M;
  // 外周に1セルの余白を取り、敷地外(=通行不可)で囲む
  let nx = Math.ceil((bb.maxX - bb.minX) / cell) + 2;
  let ny = Math.ceil((bb.maxY - bb.minY) / cell) + 2;
  if (nx * ny > ACCESS_MAX_CELLS) {
    cell *= Math.sqrt((nx * ny) / ACCESS_MAX_CELLS);
    nx = Math.ceil((bb.maxX - bb.minX) / cell) + 2;
    ny = Math.ceil((bb.maxY - bb.minY) / cell) + 2;
  }
  const x0 = bb.minX - cell;
  const y0 = bb.minY - cell;
  const blocked = new Uint8Array(nx * ny);
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const p = { x: x0 + (ix + 0.5) * cell, y: y0 + (iy + 0.5) * cell };
      let b = !pointInPolygon(p, sitePts);
      if (!b)
        for (const r of buildings)
          if (insideBuildingWithMargin(p, r, 0)) {
            b = true;
            break;
          }
      blocked[iy * nx + ix] = b ? 1 : 0;
    }
  }
  return { x0, y0, cell, nx, ny, blocked };
}

function rasterize(base: AccessBase, mask: Uint8Array, r: OrientedRect, value: number): void {
  const { x0, y0, cell, nx, ny } = base;
  const b = bbox(orientedRectCorners(r));
  const ix0 = Math.max(0, Math.floor((b.minX - x0) / cell));
  const ix1 = Math.min(nx - 1, Math.floor((b.maxX - x0) / cell));
  const iy0 = Math.max(0, Math.floor((b.minY - y0) / cell));
  const iy1 = Math.min(ny - 1, Math.floor((b.maxY - y0) / cell));
  for (let iy = iy0; iy <= iy1; iy++) {
    for (let ix = ix0; ix <= ix1; ix++) {
      const p = { x: x0 + (ix + 0.5) * cell, y: y0 + (iy + 0.5) * cell };
      if (insideBuildingWithMargin(p, r, 0)) mask[iy * nx + ix] = value;
    }
  }
}

/** 通行不可セルからの距離(m)。2パスのチャンファー距離変換(8近傍)による近似 */
function distanceToBlocked(base: AccessBase, blocked: Uint8Array): Float32Array {
  const { nx, ny, cell } = base;
  const INF = 1e9;
  const d = new Float32Array(nx * ny);
  for (let i = 0; i < d.length; i++) d[i] = blocked[i] ? 0 : INF;
  const a = cell;
  const b = cell * Math.SQRT2;
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const i = iy * nx + ix;
      if (d[i] === 0) continue;
      let v = d[i];
      if (ix > 0) v = Math.min(v, d[i - 1] + a);
      if (iy > 0) {
        v = Math.min(v, d[i - nx] + a);
        if (ix > 0) v = Math.min(v, d[i - nx - 1] + b);
        if (ix < nx - 1) v = Math.min(v, d[i - nx + 1] + b);
      }
      d[i] = v;
    }
  }
  for (let iy = ny - 1; iy >= 0; iy--) {
    for (let ix = nx - 1; ix >= 0; ix--) {
      const i = iy * nx + ix;
      if (d[i] === 0) continue;
      let v = d[i];
      if (ix < nx - 1) v = Math.min(v, d[i + 1] + a);
      if (iy < ny - 1) {
        v = Math.min(v, d[i + nx] + a);
        if (ix < nx - 1) v = Math.min(v, d[i + nx + 1] + b);
        if (ix > 0) v = Math.min(v, d[i + nx - 1] + b);
      }
      d[i] = v;
    }
  }
  return d;
}

export interface AccessResult {
  /** 判定できたかどうか(出入口がない等で判定できない場合false。そのときは全マス到達可能扱い) */
  evaluated: boolean;
  accessible: (s: ParkingSlot) => boolean;
  /** 以下は通り道の探索(openCorridors)用の内部情報。evaluated=falseのときは空 */
  reached?: Uint8Array;
  drivable?: Uint8Array;
  /** マスの短辺の外側で、走行可能域に入っていれば到達とみなす位置のセル番号 */
  probeCells?: (s: ParkingSlot) => number[];
}

/**
 * 出入口(entries)から、最小通路幅を確保しながら各マスの前までたどり着けるかを判定する。
 * entriesは出入口の通路矩形。マスは短辺のどちら側からでも到達できればよい(どちらが前かは区別しない)
 */
export function computeAccess(
  base: AccessBase,
  slots: ParkingSlot[],
  entries: OrientedRect[]
): AccessResult {
  if (entries.length === 0) return { evaluated: false, accessible: () => true };
  const { x0, y0, cell, nx, ny } = base;
  const blocked = base.blocked.slice();
  for (const s of slots) rasterize(base, blocked, s, 1);
  const dist = distanceToBlocked(base, blocked);
  // セル中心で測るため、半セル分の誤差を許容する
  const clearance = ACCESS_MIN_LANE_WIDTH_M / 2 - cell / 2;
  const drivableMask = new Uint8Array(nx * ny);
  for (let i = 0; i < drivableMask.length; i++)
    drivableMask[i] = !blocked[i] && dist[i] >= clearance ? 1 : 0;
  const drivable = (i: number) => drivableMask[i] === 1;
  const reached = new Uint8Array(nx * ny);
  const queue: number[] = [];
  const seedMask = new Uint8Array(nx * ny);
  for (const e of entries) rasterize(base, seedMask, e, 1);
  for (let i = 0; i < seedMask.length; i++) {
    if (seedMask[i] && drivable(i)) {
      reached[i] = 1;
      queue.push(i);
    }
  }
  while (queue.length) {
    const i = queue.pop() as number;
    const ix = i % nx;
    const iy = (i - ix) / nx;
    const nb = [
      ix > 0 ? i - 1 : -1,
      ix < nx - 1 ? i + 1 : -1,
      iy > 0 ? i - nx : -1,
      iy < ny - 1 ? i + nx : -1,
    ];
    for (const j of nb) {
      if (j >= 0 && !reached[j] && drivable(j)) {
        reached[j] = 1;
        queue.push(j);
      }
    }
  }
  const cellOf = (p: Point): number => {
    const ix = Math.floor((p.x - x0) / cell);
    const iy = Math.floor((p.y - y0) / cell);
    if (ix < 0 || iy < 0 || ix >= nx || iy >= ny) return -1;
    return iy * nx + ix;
  };
  // 短辺の外側を、走行可能域が始まる距離(clearance)から少し先まで調べる
  const probeCells = (s: ParkingSlot): number[] => {
    const cells: number[] = [];
    for (const side of [-1, 1]) {
      for (let d = clearance; d <= clearance + 2.5; d += cell) {
        const local = { x: 0, y: side * (s.depth / 2 + d) };
        const i = cellOf(addPoint(s.center, rotP(local, s.angle)));
        if (i >= 0) cells.push(i);
      }
    }
    return cells;
  };
  const accessible = (s: ParkingSlot): boolean => probeCells(s).some((i) => reached[i] === 1);
  return { evaluated: true, accessible, reached, drivable: drivableMask, probeCells };
}

function addPoint(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

// 4近傍の幅優先探索による、seedsからの距離(セル数)。passableが0のセルは通らない
function bfsDistance(base: AccessBase, seeds: number[], passable: Uint8Array): Int32Array {
  const { nx, ny } = base;
  const d = new Int32Array(nx * ny).fill(-1);
  const queue = new Int32Array(nx * ny);
  let head = 0;
  let tail = 0;
  for (const i of seeds) {
    if (d[i] === -1) {
      d[i] = 0;
      queue[tail++] = i;
    }
  }
  while (head < tail) {
    const i = queue[head++];
    const ix = i % nx;
    const iy = (i - ix) / nx;
    const nb = [
      ix > 0 ? i - 1 : -1,
      ix < nx - 1 ? i + 1 : -1,
      iy > 0 ? i - nx : -1,
      iy < ny - 1 ? i + nx : -1,
    ];
    for (const j of nb) {
      if (j >= 0 && d[j] === -1 && passable[j]) {
        d[j] = d[i] + 1;
        queue[tail++] = j;
      }
    }
  }
  return d;
}

const CORRIDOR_MAX_ROUNDS = 4;
const CORRIDOR_MAX_CANDIDATES = 10;
const CORRIDOR_MAX_GROUP = 3;

/**
 * 出入口からたどり着けないマスがあるとき、その手前で通り道をふさいでいるマスを探して取り除く。
 * 到達範囲と、到達できないマスの前の空間とを結ぶ最短経路の近くにあるマス(1〜3台のまとまり)を
 * 候補とし、取り除くと到達できるマスが取り除いた台数より多く増えるものを、増加が最大のものから採用する。
 * 取り除いたマスの一覧を返す(slots自体は変更しない)
 */
export function openCorridors(
  base: AccessBase,
  slots: ParkingSlot[],
  entries: OrientedRect[]
): ParkingSlot[] {
  if (entries.length === 0) return [];
  const { x0, y0, cell, nx } = base;
  const removed = new Set<ParkingSlot>();
  const current = () => slots.filter((s) => !removed.has(s));
  const countReachable = (list: ParkingSlot[]) => {
    const r = computeAccess(base, list, entries);
    return list.filter(r.accessible).length;
  };
  // 敷地内で建物以外のセル(マスは取り除けば通れるので障害物としない)
  const open = new Uint8Array(base.blocked.length);
  for (let i = 0; i < open.length; i++) open[i] = base.blocked[i] ? 0 : 1;
  for (let round = 0; round < CORRIDOR_MAX_ROUNDS; round++) {
    const list = current();
    const acc = computeAccess(base, list, entries);
    const reached = acc.reached as Uint8Array;
    const drivable = acc.drivable as Uint8Array;
    const unreachable = list.filter((s) => !acc.accessible(s));
    if (unreachable.length === 0) break;
    // 到達できないマスの前にある、走行可能だが出入口から届いていない空間
    const pocketSeeds: number[] = [];
    for (const s of unreachable)
      for (const i of (acc.probeCells as (s: ParkingSlot) => number[])(s))
        if (drivable[i] && !reached[i]) pocketSeeds.push(i);
    if (pocketSeeds.length === 0) break;
    const reachedSeeds: number[] = [];
    for (let i = 0; i < reached.length; i++) if (reached[i]) reachedSeeds.push(i);
    const distR = bfsDistance(base, reachedSeeds, open);
    const distP = bfsDistance(base, pocketSeeds, open);
    // 両方の空間を最短で結ぶ経路に近いマスほど、通り道をふさいでいる可能性が高い
    const score = (s: ParkingSlot): number => {
      const b = bbox(orientedRectCorners(s));
      let best = Number.POSITIVE_INFINITY;
      for (let y = b.minY; y <= b.maxY; y += cell) {
        for (let x = b.minX; x <= b.maxX; x += cell) {
          if (!insideBuildingWithMargin({ x, y }, s, 0)) continue;
          const i = Math.floor((y - y0) / cell) * nx + Math.floor((x - x0) / cell);
          if (i < 0 || i >= distR.length || distR[i] < 0 || distP[i] < 0) continue;
          best = Math.min(best, distR[i] + distP[i]);
        }
      }
      return best;
    };
    const reachableList = list.filter((s) => acc.accessible(s));
    const scored = reachableList
      .map((s) => ({ s, v: score(s) }))
      .filter((x) => Number.isFinite(x.v))
      .sort((a, b) => a.v - b.v);
    if (scored.length === 0) break;
    const limit = scored[0].v + Math.ceil((ACCESS_MIN_LANE_WIDTH_M * 2) / cell);
    const candidates = scored.filter((x) => x.v <= limit).slice(0, CORRIDOR_MAX_CANDIDATES);
    const baseline = reachableList.length;
    let bestGroup: ParkingSlot[] | null = null;
    let bestGain = 0;
    const tried = new Set<string>();
    for (const { s } of candidates) {
      // 候補と、その近くのマスを近い順に1〜3台まとめて試す(1台では開かない通り道もあるため)
      const near = list
        .filter((o) => o !== s && Math.hypot(o.center.x - s.center.x, o.center.y - s.center.y) < 6)
        .sort(
          (a, b) =>
            Math.hypot(a.center.x - s.center.x, a.center.y - s.center.y) -
            Math.hypot(b.center.x - s.center.x, b.center.y - s.center.y)
        );
      for (let k = 0; k < CORRIDOR_MAX_GROUP; k++) {
        const group = [s, ...near.slice(0, k)];
        if (group.length !== k + 1) break;
        const key = group
          .map((g) => list.indexOf(g))
          .sort((a, b) => a - b)
          .join(',');
        if (tried.has(key)) continue;
        tried.add(key);
        const rest = list.filter((o) => !group.includes(o));
        const gain = countReachable(rest) - baseline;
        if (gain > bestGain) {
          bestGain = gain;
          bestGroup = group;
        }
      }
    }
    if (!bestGroup) break;
    for (const g of bestGroup) removed.add(g);
  }
  return [...removed];
}

// 折れ線の点列を、許容誤差eps以内で間引く(Douglas-Peucker)
function simplifyPath(pts: Point[], eps: number): Point[] {
  if (pts.length <= 2) return pts;
  const a = pts[0];
  const b = pts[pts.length - 1];
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  let maxD = 0;
  let idx = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i];
    const d = Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y)) / len;
    if (d > maxD) {
      maxD = d;
      idx = i;
    }
  }
  if (maxD <= eps) return [a, b];
  return [
    ...simplifyPath(pts.slice(0, idx + 1), eps).slice(0, -1),
    ...simplifyPath(pts.slice(idx), eps),
  ];
}

/**
 * 出入口から届いている範囲(reached)から、到達できないマスの前(targets)までの、幅widthMの通り道を計画する。
 * 建物・敷地外から幅の半分以上離れたセルだけを通る最短経路(8近傍)を求め、直線区間ごとの矩形で返す。
 * 経路が見つからなければnull
 */
export function planCorridor(
  base: AccessBase,
  reached: Uint8Array,
  targets: number[],
  widthM: number
): OrientedRect[] | null {
  const { x0, y0, cell, nx, ny } = base;
  if (!base.baseDist) base.baseDist = distanceToBlocked(base, base.blocked);
  const dist = base.baseDist;
  const need = widthM / 2 - cell / 2;
  const ok = (i: number) => dist[i] >= need;
  const isTarget = new Uint8Array(nx * ny);
  let anyTarget = false;
  for (const t of targets) {
    // 目標セルの近く(マス1台分程度)で通れるセルも目標にする(マスの前が狭くても、手前まで通せればよい)
    const tx = t % nx;
    const ty = (t - tx) / nx;
    const r = Math.ceil(2 / cell);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const ix = tx + dx;
        const iy = ty + dy;
        if (ix < 0 || iy < 0 || ix >= nx || iy >= ny) continue;
        const j = iy * nx + ix;
        if (!reached[j] && ok(j)) {
          isTarget[j] = 1;
          anyTarget = true;
        }
      }
    }
  }
  if (!anyTarget) return null;
  const parent = new Int32Array(nx * ny).fill(-2);
  const queue = new Int32Array(nx * ny);
  let head = 0;
  let tail = 0;
  for (let i = 0; i < reached.length; i++) {
    if (reached[i] && ok(i)) {
      parent[i] = -1;
      queue[tail++] = i;
    }
  }
  let hit = -1;
  while (head < tail && hit < 0) {
    const i = queue[head++];
    const ix = i % nx;
    const iy = (i - ix) / nx;
    for (let dy = -1; dy <= 1 && hit < 0; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const jx = ix + dx;
        const jy = iy + dy;
        if (jx < 0 || jy < 0 || jx >= nx || jy >= ny) continue;
        const j = jy * nx + jx;
        if (parent[j] !== -2 || !ok(j)) continue;
        parent[j] = i;
        if (isTarget[j]) {
          hit = j;
          break;
        }
        queue[tail++] = j;
      }
    }
  }
  if (hit < 0) return null;
  const path: Point[] = [];
  for (let i = hit; i >= 0; i = parent[i]) {
    const ix = i % nx;
    const iy = (i - ix) / nx;
    path.push({ x: x0 + (ix + 0.5) * cell, y: y0 + (iy + 0.5) * cell });
  }
  path.reverse();
  const pts = simplifyPath(path, 1.0);
  const rects: OrientedRect[] = [];
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k];
    const b = pts[k + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 1e-6) continue;
    // 区間の継ぎ目で隙間ができないよう、両端を幅の半分ずつ延ばす
    rects.push({
      center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      width: len + widthM,
      depth: widthM,
      angle: Math.atan2(b.y - a.y, b.x - a.x),
    });
  }
  return rects.length ? rects : null;
}
